import { useEffect, useCallback, useRef } from "react"
import { useDispatch } from "react-redux"
import { setLatestVersion, setCurrentVersion } from "../state/versionSlice"

interface VersionManifest {
  version: string
  hash: string
  coreHash: string
  deployedAt: string
}

const VERSION_CHECK_INTERVAL = 5 * 60 * 1000 // 5 minutes
const MAX_BACKOFF = 30 * 60 * 1000 // 30 minutes
const LAST_CHECK_KEY = "version_last_check"

// Determine environment from build-time config
const getEnvironment = (): "latest" | "preview" => {
  return __APP_ENV__ === "prod" ? "latest" : "preview"
}

const getVersionManifestUrl = (): string => {
  // In development, use the local version.json from the public folder.
  // Change its hash/coreHash values to simulate a new deploy.
  if (__APP_ENV__ === "development") {
    return "/version.json"
  }
  const env = getEnvironment()
  return `https://hydrologiq-prod-dashboard.s3.eu-west-2.amazonaws.com/sections/hub/${env}.json`
}

/**
 * Hook that polls S3 for version updates and dispatches results to Redux.
 * Compares both the hub hash and core submodule hash — a change in either
 * triggers the refresh toast.
 *
 * Only runs when `enabled` is true (i.e. user is authenticated).
 * Uses exponential backoff on consecutive failures.
 */
export const useVersionCheck = (enabled: boolean) => {
  const dispatch = useDispatch()
  const consecutiveFailuresRef = useRef(0)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const getBackoffInterval = useCallback(() => {
    if (consecutiveFailuresRef.current === 0) return VERSION_CHECK_INTERVAL
    const backoff = VERSION_CHECK_INTERVAL * Math.pow(2, consecutiveFailuresRef.current)
    return Math.min(backoff, MAX_BACKOFF)
  }, [])

  const checkVersion = useCallback(async () => {
    try {
      // Respect 5-minute cache between successful checks
      const lastCheckStr = localStorage.getItem(LAST_CHECK_KEY)
      const now = Date.now()

      if (lastCheckStr) {
        const lastCheck = parseInt(lastCheckStr, 10)
        if (now - lastCheck < VERSION_CHECK_INTERVAL) {
          return
        }
      }

      const response = await fetch(getVersionManifestUrl(), {
        cache: "no-cache",
        headers: { "Cache-Control": "no-cache" },
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch version manifest: ${response.status}`)
      }

      const manifest: VersionManifest = await response.json()

      // Success — reset backoff
      consecutiveFailuresRef.current = 0
      localStorage.setItem(LAST_CHECK_KEY, now.toString())

      // Dispatch to Redux — the slice handles comparison logic
      dispatch(
        setLatestVersion({
          version: manifest.version,
          hash: manifest.hash,
          coreHash: manifest.coreHash,
        }),
      )
    } catch (err) {
      consecutiveFailuresRef.current += 1
      console.error(
        `Version check failed (attempt ${consecutiveFailuresRef.current}, next in ${Math.round(getBackoffInterval() / 1000)}s):`,
        err,
      )
    }
  }, [dispatch, getBackoffInterval])

  useEffect(() => {
    if (!enabled) return

    // Set current build info on mount
    dispatch(
      setCurrentVersion({
        version: __APP_VERSION__,
        hash: __APP_HASH__,
        coreHash: __CORE_HASH__,
      }),
    )

    // Check immediately
    checkVersion()

    // Schedule recurring checks with backoff-aware interval
    const scheduleNext = () => {
      timerRef.current = setTimeout(async () => {
        await checkVersion()
        scheduleNext()
      }, getBackoffInterval())
    }
    scheduleNext()

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [enabled, checkVersion, dispatch, getBackoffInterval])
}

/**
 * Clear all service-worker caches and unregister the SW, then hard-reload.
 * This ensures the browser fetches the new index.html (which references
 * new content-hashed asset filenames) instead of serving stale cached files.
 */
export const refreshApp = async () => {
  try {
    // 1. Delete all Workbox / SW caches so stale assets aren't served
    if ("caches" in window) {
      const cacheNames = await caches.keys()
      await Promise.all(cacheNames.map((name) => caches.delete(name)))
    }

    // 2. Tell the active service worker to skip waiting and activate immediately.
    //    On the next load vite-plugin-pwa will register the new SW.
    if ("serviceWorker" in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations()
      await Promise.all(registrations.map((r) => r.unregister()))
    }
  } catch (err) {
    console.warn("Cache/SW cleanup failed, reloading anyway:", err)
  }

  // 3. Force a server fetch for the page (bypass HTTP cache)
  window.location.reload()
}
